import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Instagram } from "lucide-react";
import { IMAGES, CONTACT, AREAS } from "../../lib/site";

export default function FinalCTA() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <>
      {/* Cinematic CTA */}
      <section ref={ref} className="relative h-[90vh] min-h-[560px] overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0 scale-125">
          <img src="/med-kitchen.png" alt="Luxury Mediterranean home kitchen with sea view" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-ocean-teal/45" />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col items-center justify-center text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif font-light text-white text-4xl sm:text-5xl md:text-6xl max-w-3xl leading-tight"
          >
            Every Space Deserves to Feel Immaculate.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3 }}
            className="mt-6 font-sans font-light text-warm-white/85 text-lg"
          >
            Every client deserves a premium experience.
          </motion.p>
          <motion.a
            href="#quote"
            data-testid="cta-book-btn"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.45 }}
            className="mt-10 rounded-full bg-ocean-teal border border-warm-white/40 px-9 py-4 text-sm font-medium tracking-wide text-warm-white hover:bg-warm-white hover:border-warm-white hover:text-ocean-teal active:bg-warm-white active:text-ocean-teal transition-colors"
          >
            Book Your Boutique Cleaning Experience
          </motion.a>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-warm-white text-charcoal py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-3">
              <img src={IMAGES.logo} alt="Casa Galênê" className="h-16 w-16 object-contain" />
              <span>
                <span className="block font-serif text-xl text-ocean-teal">Casa Galênê</span>
                <span className="block text-[10px] tracking-[0.3em] uppercase text-antique-gold">Cleaning</span>
              </span>
            </div>
            <p className="mt-6 font-sans font-light text-sm text-ocean-teal/80 max-w-xs leading-relaxed">
              Boutique cleaning &amp; estate care inspired by Mediterranean living. Where luxury meets serenity.
            </p>
          </div>

          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-antique-gold">Contact</span>
            <div className="mt-5 space-y-3 font-sans font-light text-sm text-charcoal/75">
              <a href={`mailto:${CONTACT.email}`} className="block hover:text-ocean-teal transition-colors">{CONTACT.email}</a>
              <a href={`tel:${CONTACT.phoneRaw}`} className="block hover:text-ocean-teal transition-colors">{CONTACT.phone}</a>
              <p>{CONTACT.base}</p>
              <a
                href="https://www.instagram.com/casagalenecleaning/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                aria-label="Follow Casa Galênê Cleaning on Instagram"
                className="mt-2 inline-flex items-center gap-2 text-ocean-teal hover:text-antique-gold transition-colors"
              >
                <Instagram size={18} strokeWidth={1.5} />
                <span className="text-xs tracking-widest uppercase">@casagalenecleaning</span>
              </a>
            </div>
          </div>

          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-antique-gold">Service Areas</span>
            <div className="mt-5 space-y-2 font-sans font-light text-sm text-charcoal/75">
              {AREAS.map((a) => <p key={a}>{a}</p>)}
            </div>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-14 pt-8 border-t border-border-stone flex flex-col sm:flex-row justify-between gap-3 text-xs text-charcoal/40 font-sans">
          <span>© {new Date().getFullYear()} Casa Galênê Cleaning. All rights reserved.</span>
          <span>Where Luxury Meets Serenity.</span>
        </div>
      </footer>
    </>
  );
}
